import { Link } from "react-router-dom";
import { Target, Eye, Compass, Handshake, ShieldCheck, TrendingUp, ArrowRight } from "lucide-react";
import { useReveal } from "../lib/reveal";

const aboutImg =
  "https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NzkwODQyMjF8MA&ixlib=rb-4.1.0&q=85";

const values = [
  { icon: ShieldCheck, title: "Integrity", desc: "Every trade conducted with absolute transparency." },
  { icon: Handshake, title: "Partnership", desc: "Long-term relationships over one-off transactions." },
  { icon: TrendingUp, title: "Growth", desc: "Helping clients scale into new markets confidently." },
  { icon: Compass, title: "Precision", desc: "Meticulous documentation and on-time execution." },
];

export default function About() {
  const ref = useReveal();

  return (
    <div ref={ref} data-testid="about-page">
      {/* HEADER */}
      <section className="relative bg-[#0E2A3F] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.9) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#F26F21]/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <span className="reveal text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
            About Us
          </span>
          <h1 className="reveal mt-5 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl leading-tight">
            Who We Are.
          </h1>
          <p className="reveal mt-7 max-w-2xl text-slate-300 text-base lg:text-lg leading-relaxed">
            VRVR Import and Exports was founded with a vision to simplify global trade for
            businesses of all sizes. We are committed to providing high-quality import and
            export solutions that help our clients grow and thrive.
          </p>
        </div>
      </section>

      {/* MAIN STORY */}
      <section className="py-24 lg:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 reveal">
            <div className="relative">
              <div className="absolute -inset-3 rounded-2xl border-2 border-[#F26F21]/30 -rotate-2" />
              <img
                src={aboutImg}
                alt="Business handshake"
                className="relative rounded-2xl object-cover w-full h-[520px] shadow-xl"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="reveal">
              <span className="text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
                Our Story
              </span>
              <h2 className="mt-4 font-[Outfit] text-3xl lg:text-4xl font-bold text-[#0E2A3F] tracking-tight accent-underline">
                Built on trust. Powered by global reach.
              </h2>
            </div>

            <div className="mt-12 space-y-8">
              <div className="reveal rounded-2xl bg-white border border-slate-200/70 shadow-sm p-8 lift">
                <div className="flex items-start gap-5">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#F26F21]/15 text-[#F26F21] ring-1 ring-[#F26F21]/30 shrink-0">
                    <Target className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-[Outfit] text-xl font-semibold text-[#0E2A3F]">Our Mission</h3>
                    <p className="mt-3 text-[#475569] leading-relaxed">
                      To provide seamless, reliable, and cost-effective import and export
                      services while maintaining the highest standards of integrity and
                      professionalism.
                    </p>
                  </div>
                </div>
              </div>

              <div className="reveal rounded-2xl bg-[#0E2A3F] text-white p-8 lift relative overflow-hidden">
                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-[#F26F21]/20 blur-2xl" />
                <div className="relative flex items-start gap-5">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-white/10 text-[#F26F21] ring-1 ring-white/15 shrink-0">
                    <Eye className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-[Outfit] text-xl font-semibold text-white">Our Vision</h3>
                    <p className="mt-3 text-slate-300 leading-relaxed">
                      To become a globally recognized trading partner, connecting businesses and
                      markets worldwide with trust and efficiency.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl reveal">
            <span className="text-[11px] uppercase tracking-[0.24em] text-[#F26F21] font-semibold">
              Our Values
            </span>
            <h2 className="mt-4 font-[Outfit] text-3xl lg:text-4xl font-bold text-[#0E2A3F] tracking-tight">
              The principles guiding every shipment.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div
                key={v.title}
                data-testid={`value-card-${i}`}
                className="reveal lift group relative rounded-xl bg-[#F8FAFC] border border-slate-200/70 p-7 hover:border-[#F26F21]/60 hover:bg-white"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-white text-[#0E2A3F] ring-1 ring-slate-200 group-hover:bg-[#0E2A3F] group-hover:text-white transition-colors">
                  <v.icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <h3 className="mt-6 font-[Outfit] text-lg font-semibold text-[#0E2A3F]">{v.title}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal rounded-2xl border border-slate-200 bg-white p-10 lg:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm">
            <div>
              <h3 className="font-[Outfit] text-2xl lg:text-3xl font-bold text-[#0E2A3F] tracking-tight">
                Let&apos;s discuss your next shipment.
              </h3>
              <p className="mt-2 text-[#475569]">Our specialists are a message away.</p>
            </div>
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 rounded-md bg-[#F26F21] px-7 py-4 text-sm font-semibold text-white hover:bg-[#D95B14] transition-colors shrink-0"
            >
              Contact Our Team <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
